import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import Button from "../../atoms/Button";
import Card from "../Card";
import PanelInputField from "../PanelInputField";
import PopupOverlay from "../../organisms/Background/PopupOverlay";
import {
    accountDeletionService,
    createAccountDeletionIdempotencyKey,
} from "../../../services/accountDeletionService";
import {
    clearAccountDeletionSsoCallback,
    rememberAccountDeletionSsoCallback,
    navigateToAccountDeletionSsoProvider,
    requireSafeAccountDeletionAuthorizeUrl,
} from "../../../services/accountDeletionSsoCallback";
import {
    getAccountDeletionActionGrant,
    getAccountDeletionChallengeId,
    getAccountDeletionCsrfToken,
} from "../../../services/accountDeletionResponseValidation";
import { submitAccountDeletion } from "../../../services/accountDeletionSubmission";
import { fetchCurrentBillingSubscription } from "../../../services/api/billing";
import { useAccountDeletionSsoProviders } from "../../../hooks/useAccountDeletionSsoProviders";
import { useClearSensitiveRefsOnPageHide } from "../../../hooks/useClearSensitiveRefsOnPageHide";
import AccountDeletionBillingImpactNotice, {
    ACCOUNT_DELETION_BILLING_IMPACTS,
    normalizeAccountDeletionBillingImpact,
} from "../AccountDeletionBillingImpactNotice";
import "./index.scss";

const CONFIRMATION = "DELETE";
const AUTHORIZATION_STEP = "authorization";
const CHALLENGE_STEP = "challenge";
const FINAL_STEP = "final";

const ERROR_KEYS = Object.freeze({
    AUTHENTICATION_REQUIRED: "profile.accountDeletion.errors.authenticationRequired",
    REAUTHENTICATION_REQUIRED: "profile.accountDeletion.errors.reauthenticationRequired",
    REQUEST_ORIGIN_REJECTED: "profile.accountDeletion.errors.originRejected",
    IDEMPOTENCY_CONFLICT: "profile.accountDeletion.errors.conflict",
    DELETION_ALREADY_PENDING: "profile.accountDeletion.errors.alreadyPending",
    RATE_LIMITED: "profile.accountDeletion.errors.rateLimited",
    DEPENDENCY_UNAVAILABLE: "profile.accountDeletion.errors.unavailable",
});

const resolveErrorKey = (error) => (
    ERROR_KEYS[error?.code] || "profile.accountDeletion.errors.generic"
);

const previewTextItems = (preview) => [
    ["personalContentTreatment", "profile.accountDeletion.preview.personalContentTreatment"],
    ["sharedContentTreatment", "profile.accountDeletion.preview.sharedContentTreatment"],
].flatMap(([field, labelKey]) => (
    typeof preview?.[field] === "string" && preview[field].trim()
        ? [{ field, labelKey, value: preview[field].trim() }]
        : []
));

const recoveryWindowDaysFromPreview = (preview) => (
    Number.isSafeInteger(preview?.recoveryWindowDays)
        && preview.recoveryWindowDays > 0
        && preview.recoveryWindowDays <= 3650
        ? preview.recoveryWindowDays
        : null
);

const billingImpactErrorMetadata = (error) => ({
    code: typeof error?.body?.code === "string"
        ? error.body.code
        : typeof error?.response?.data?.code === "string"
            ? error.response.data.code
            : null,
    requestId: typeof error?.body?.requestId === "string"
        ? error.body.requestId
        : typeof error?.response?.data?.requestId === "string"
            ? error.response.data.requestId
            : null,
});

const AccountDeletionModal = ({ onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const ssoProviders = useAccountDeletionSsoProviders();
    const [step, setStep] = useState(AUTHORIZATION_STEP);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorKey, setErrorKey] = useState(null);
    const [preview, setPreview] = useState(null);
    const [csrfToken, setCsrfToken] = useState(null);
    const [password, setPassword] = useState("");
    const [typedConfirmation, setTypedConfirmation] = useState("");
    const [challengeId, setChallengeId] = useState(null);
    const [verificationCode, setVerificationCode] = useState("");
    const [billingImpactState, setBillingImpactState] = useState({
        impact: ACCOUNT_DELETION_BILLING_IMPACTS.UNKNOWN,
        isLoading: true,
        error: null,
    });
    const reauthTokenRef = useRef(null);
    const idempotencyKeyRef = useRef(null);
    const mountedRef = useRef(true);
    useClearSensitiveRefsOnPageHide(reauthTokenRef, idempotencyKeyRef);

    const refreshBillingImpact = useCallback(async () => {
        setBillingImpactState({
            impact: ACCOUNT_DELETION_BILLING_IMPACTS.UNKNOWN,
            isLoading: true,
            error: null,
        });
        try {
            const summary = await fetchCurrentBillingSubscription();
            if (!mountedRef.current) {
                return;
            }
            const impact = normalizeAccountDeletionBillingImpact(summary?.accountDeletionBillingImpact);
            setBillingImpactState({
                impact,
                isLoading: false,
                error: impact === ACCOUNT_DELETION_BILLING_IMPACTS.UNKNOWN
                    ? { code: "MALFORMED_BILLING_IMPACT" }
                    : null,
            });
        } catch (error) {
            if (!mountedRef.current) {
                return;
            }
            setBillingImpactState({
                impact: ACCOUNT_DELETION_BILLING_IMPACTS.UNKNOWN,
                isLoading: false,
                error: billingImpactErrorMetadata(error),
            });
        }
    }, []);

    const previewItems = useMemo(() => previewTextItems(preview), [preview]);
    const ownershipReviewValueKey = preview?.ownershipReviewRequired === true
        ? "profile.accountDeletion.preview.required"
        : preview?.ownershipReviewRequired === false
            ? "profile.accountDeletion.preview.notRequired"
            : null;
    const recoveryWindowDays = recoveryWindowDaysFromPreview(preview);
    const hasPreviewDetails = previewItems.length > 0
        || ownershipReviewValueKey !== null
        || recoveryWindowDays !== null;

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    useEffect(() => {
        let active = true;
        refreshBillingImpact();
        Promise.all([
            accountDeletionService.getCsrfToken(),
            accountDeletionService.getDeletionPreview(),
        ])
            .then(([csrfResponse, previewResponse]) => {
                if (!active) {
                    return;
                }
                const nextCsrfToken = getAccountDeletionCsrfToken(csrfResponse);
                if (!nextCsrfToken) {
                    setErrorKey("profile.accountDeletion.errors.unavailable");
                    return;
                }
                setCsrfToken(nextCsrfToken);
                setPreview(previewResponse);
            })
            .catch((error) => {
                if (active) {
                    setErrorKey(resolveErrorKey(error));
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [refreshBillingImpact]);

    useEffect(() => {
        if (isLoading || typeof document === "undefined") {
            return;
        }
        const focusTargetId = step === AUTHORIZATION_STEP
            ? "account-deletion-password"
            : step === CHALLENGE_STEP
                ? "account-deletion-code"
                : "account-deletion-final-submit";
        const focusTarget = document.getElementById(focusTargetId);
        try {
            focusTarget?.focus({ preventScroll: true });
        } catch (_) {
            focusTarget?.focus();
        }
    }, [isLoading, step]);

    const beginAuthorization = async (event) => {
        event.preventDefault();
        if (!password || typedConfirmation !== CONFIRMATION || !csrfToken) {
            setErrorKey("profile.accountDeletion.errors.confirmationRequired");
            return;
        }

        setIsSubmitting(true);
        setErrorKey(null);
        try {
            const response = await accountDeletionService.startPasswordAuthorization({
                password,
                confirmation: CONFIRMATION,
                csrfToken,
            });
            const nextChallengeId = getAccountDeletionChallengeId(response);
            if (!nextChallengeId) {
                setErrorKey("profile.accountDeletion.errors.unavailable");
                return;
            }
            setPassword("");
            setChallengeId(nextChallengeId);
            setStep(CHALLENGE_STEP);
        } catch (error) {
            setErrorKey(resolveErrorKey(error));
        } finally {
            setPassword("");
            setIsSubmitting(false);
        }
    };

    const beginSsoAuthorization = async (provider) => {
        if (!ssoProviders.includes(provider)
            || typedConfirmation !== CONFIRMATION
            || !csrfToken) {
            setErrorKey("profile.accountDeletion.errors.ssoConfirmationRequired");
            return;
        }

        setIsSubmitting(true);
        setErrorKey(null);
        clearAccountDeletionSsoCallback();
        try {
            const response = await accountDeletionService.startSsoAuthorization({
                provider,
                confirmation: CONFIRMATION,
                csrfToken,
            });
            const authorizeUrl = requireSafeAccountDeletionAuthorizeUrl(response?.authorizeUrl);
            rememberAccountDeletionSsoCallback({ flow: "active", provider });
            setPassword("");
            navigateToAccountDeletionSsoProvider(authorizeUrl);
        } catch (error) {
            clearAccountDeletionSsoCallback();
            setErrorKey(resolveErrorKey(error));
            setIsSubmitting(false);
        }
    };

    const completeAuthorization = async (event) => {
        event.preventDefault();
        if (!/^\d{6}$/.test(verificationCode) || !challengeId || !csrfToken) {
            setErrorKey("profile.accountDeletion.errors.invalidCode");
            return;
        }

        setIsSubmitting(true);
        setErrorKey(null);
        try {
            const response = await accountDeletionService.completePasswordAuthorization({
                challengeId,
                code: verificationCode,
                confirmation: CONFIRMATION,
                csrfToken,
            });
            const reauthToken = getAccountDeletionActionGrant(response);
            if (!reauthToken) {
                setErrorKey("profile.accountDeletion.errors.unavailable");
                return;
            }
            reauthTokenRef.current = reauthToken;
            idempotencyKeyRef.current = createAccountDeletionIdempotencyKey();
            setVerificationCode("");
            setStep(FINAL_STEP);
            refreshBillingImpact();
        } catch (error) {
            setErrorKey(resolveErrorKey(error));
        } finally {
            setVerificationCode("");
            setIsSubmitting(false);
        }
    };

    const createDeletion = async () => {
        if (!reauthTokenRef.current || !idempotencyKeyRef.current || !csrfToken) {
            setErrorKey("profile.accountDeletion.errors.reauthenticationRequired");
            return;
        }

        setIsSubmitting(true);
        setErrorKey(null);
        try {
            await submitAccountDeletion({
                csrfToken,
                reauthToken: reauthTokenRef.current,
                idempotencyKey: idempotencyKeyRef.current,
                dispatch,
            });
            reauthTokenRef.current = null;
            idempotencyKeyRef.current = null;
            setCsrfToken(null);
            setChallengeId(null);
        } catch (error) {
            setErrorKey(resolveErrorKey(error));
            if (
                error?.code === "REAUTHENTICATION_REQUIRED"
                || error?.code === "IDEMPOTENCY_CONFLICT"
            ) {
                reauthTokenRef.current = null;
                idempotencyKeyRef.current = null;
                setChallengeId(null);
                setStep(AUTHORIZATION_STEP);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isBusy = isLoading || isSubmitting;
    const title = t("profile.accountDeletion.modalTitle");
    const portalTarget = typeof document !== "undefined"
        ? document.getElementById("portal")
        : null;

    const content = (
        <PopupOverlay
            onClose={onClose}
            closeOnEsc={!isBusy}
            closeOnOverlayClick={!isBusy}
            aria-label={title}
        >
            <Card
                className="account-deletion-card"
                title={<strong className="account-deletion-card__title">{title}</strong>}
                body={(
                    <div className="account-deletion-card__content" aria-busy={isBusy}>
                        {errorKey && (
                            <div className="account-deletion-card__error" role="alert">
                                {t(errorKey)}
                            </div>
                        )}

                        {isLoading ? (
                            <p
                                className="account-deletion-card__loading"
                                role="status"
                                aria-live="polite"
                            >
                                {t("profile.accountDeletion.loading")}
                            </p>
                        ) : step === AUTHORIZATION_STEP ? (
                            <form
                                id="account-deletion-authorization-form"
                                className="account-deletion-card__step"
                                onSubmit={beginAuthorization}
                            >
                                <p>{t("profile.accountDeletion.explanation")}</p>
                                <p className="account-deletion-card__warning">
                                    {t("profile.accountDeletion.sharedContentNotice")}
                                </p>
                                <AccountDeletionBillingImpactNotice
                                    recoveryWindowDays={recoveryWindowDays}
                                    impact={billingImpactState.impact}
                                    isLoading={billingImpactState.isLoading}
                                    error={billingImpactState.error}
                                />
                                {hasPreviewDetails && (
                                    <dl className="account-deletion-card__preview">
                                        {ownershipReviewValueKey && (
                                            <div className="account-deletion-card__preview-row">
                                                <dt>{t("profile.accountDeletion.preview.ownershipReview")}</dt>
                                                <dd>{t(ownershipReviewValueKey)}</dd>
                                            </div>
                                        )}
                                        {recoveryWindowDays !== null && (
                                            <div className="account-deletion-card__preview-row">
                                                <dt>{t("profile.accountDeletion.preview.recoveryWindow")}</dt>
                                                <dd>{recoveryWindowDays}</dd>
                                            </div>
                                        )}
                                        {/* These two values are free text from the
                                            deletion-preview endpoint — full sentences, not
                                            statuses — so they stack rather than sharing a
                                            baseline with their label. */}
                                        {previewItems.map((item) => (
                                            <div
                                                key={item.field}
                                                className="account-deletion-card__preview-row account-deletion-card__preview-row--prose"
                                            >
                                                <dt>{t(item.labelKey)}</dt>
                                                <dd>{item.value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                )}
                                <PanelInputField
                                    id="account-deletion-password"
                                    label={t("profile.accountDeletion.fields.password")}
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(event) => {
                                        setPassword(event.target.value);
                                        setErrorKey(null);
                                    }}
                                    disabled={isBusy}
                                />
                                <PanelInputField
                                    id="account-deletion-confirmation"
                                    label={t("profile.accountDeletion.fields.confirmation")}
                                    value={typedConfirmation}
                                    autoComplete="off"
                                    spellCheck="false"
                                    onChange={(event) => {
                                        setTypedConfirmation(event.target.value);
                                        setErrorKey(null);
                                    }}
                                    disabled={isBusy}
                                />
                                <div className="account-deletion-card__sso-actions">
                                    {ssoProviders.map((provider) => (
                                        <Button
                                            key={provider}
                                            className="account-deletion-card__sso-button"
                                            type="button"
                                            variant="secondary"
                                            shape="pill"
                                            disabled={isBusy || !csrfToken}
                                            onClick={() => beginSsoAuthorization(provider)}
                                        >
                                            {t(`profile.accountDeletion.actions.${provider}`)}
                                        </Button>
                                    ))}
                                </div>
                            </form>
                        ) : step === CHALLENGE_STEP ? (
                            <form
                                id="account-deletion-challenge-form"
                                className="account-deletion-card__step"
                                onSubmit={completeAuthorization}
                            >
                                <p>{t("profile.accountDeletion.challenge.instructions")}</p>
                                <PanelInputField
                                    id="account-deletion-code"
                                    label={t("profile.accountDeletion.fields.code")}
                                    value={verificationCode}
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    autoComplete="one-time-code"
                                    onChange={(event) => {
                                        setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                                        setErrorKey(null);
                                    }}
                                    disabled={isBusy}
                                />
                            </form>
                        ) : (
                            <div className="account-deletion-card__step account-deletion-card__final">
                                <p className="account-deletion-card__warning">
                                    {t("profile.accountDeletion.finalWarning")}
                                </p>
                                <AccountDeletionBillingImpactNotice
                                    recoveryWindowDays={recoveryWindowDays}
                                    impact={billingImpactState.impact}
                                    isLoading={billingImpactState.isLoading}
                                    error={billingImpactState.error}
                                />
                                <p>{t("profile.accountDeletion.finalRecoveryNotice")}</p>
                            </div>
                        )}
                    </div>
                )}
                footer={(
                    <div className="account-deletion-card__actions">
                        <Button
                            className="account-deletion-card__action account-deletion-card__action--secondary"
                            variant="secondary"
                            shape="pill"
                            onClick={onClose}
                            disabled={isBusy}
                        >
                            {t("common.cancel")}
                        </Button>
                        {step === AUTHORIZATION_STEP ? (
                            <Button
                                className="account-deletion-card__action account-deletion-card__action--danger"
                                variant="danger"
                                shape="pill"
                                type="submit"
                                form="account-deletion-authorization-form"
                                disabled={isBusy || !csrfToken}
                            >
                                {isSubmitting
                                    ? t("profile.accountDeletion.actions.sending")
                                    : t("profile.accountDeletion.actions.sendCode")}
                            </Button>
                        ) : step === CHALLENGE_STEP ? (
                            <Button
                                className="account-deletion-card__action account-deletion-card__action--danger"
                                variant="danger"
                                shape="pill"
                                type="submit"
                                form="account-deletion-challenge-form"
                                disabled={isBusy}
                            >
                                {isSubmitting
                                    ? t("profile.accountDeletion.actions.verifying")
                                    : t("profile.accountDeletion.actions.verify")}
                            </Button>
                        ) : (
                            <Button
                                id="account-deletion-final-submit"
                                className="account-deletion-card__action account-deletion-card__action--danger"
                                variant="danger"
                                shape="pill"
                                onClick={createDeletion}
                                disabled={isBusy}
                            >
                                {isSubmitting
                                    ? t("profile.accountDeletion.actions.deleting")
                                    : t("profile.accountDeletion.actions.delete")}
                            </Button>
                        )}
                    </div>
                )}
            />
        </PopupOverlay>
    );

    return portalTarget ? createPortal(content, portalTarget) : content;
};

AccountDeletionModal.propTypes = {
    onClose: PropTypes.func.isRequired,
};

export default AccountDeletionModal;
